import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SITE = 'https://dailyuaejobs.com';
const DEFAULT_DESC =
  'Explore UAE-focused CV optimization, ATS support, interview preparation, salary insights, verified career information and job-search guidance.';

// path -> { title, description, noindex }
const ROUTE_SEO = {
  '/': {
    title: 'Daily UAE Jobs | CV Checker & UAE Career Support',
    description:
      'Improve your UAE job search with Daily UAE Jobs. Check your ATS CV score, optimize your resume, prepare for interviews and explore career tools.',
  },
  '/career-tools': {
    title: 'UAE Career Tools | CV, LinkedIn & Interview Prep',
    description:
      'Explore career tools for UAE job seekers, including ATS CV checking, CV creation, LinkedIn optimization, AI mock interviews and career coaching.',
  },
  '/cv-checker': {
    title: 'Free ATS CV Checker UAE | Daily UAE Jobs',
    description:
      "Check your CV's ATS readiness with Daily UAE Jobs. Identify resume formatting issues, missing keywords and practical improvements for UAE job applications.",
  },
  '/pricing': {
    title: 'CV & Career Support Packages UAE | Daily UAE Jobs',
    description:
      'Compare Daily UAE Jobs career packages from AED 0 to AED 999, including CV optimization, LinkedIn support, AI interviews and personalized coaching.',
  },
  '/salary': {
    title: 'UAE Salary Guide 2026 | Salaries and Skills by Role',
    description:
      'Explore 2026 UAE salary ranges, in-demand skills and experience levels by role to benchmark your pay and target the right jobs.',
  },
  '/success-stories': {
    title: 'UAE Career Success Stories | Daily UAE Jobs',
    description:
      'Explore genuine career journeys and experiences shared by the Daily UAE Jobs community, with practical inspiration for your UAE job search.',
  },
  '/community': {
    title: 'UAE Job Seekers Community | Daily UAE Jobs',
    description:
      'Connect with the Daily UAE Jobs community for career guidance, job-search insights, CV tips, interview preparation and UAE employment updates.',
  },
  '/about': {
    title: 'About Daily UAE Jobs | Your UAE Career Partner',
    description:
      'Learn about Daily UAE Jobs, a UAE-focused career support platform helping job seekers improve CVs, prepare for interviews and plan their job search.',
  },
  '/contact': {
    title: 'Contact Daily UAE Jobs | Career Support UAE',
    description:
      'Contact Daily UAE Jobs for questions about CV services, career packages, account access, partnerships and career support in the UAE.',
  },
  '/b2b': {
    title: 'Career Solutions for UAE Training Centers and Employers',
    description:
      'Career and CV solutions for UAE training centers, universities and employers preparing candidates for the UAE job market.',
  },
  '/affiliate': {
    title: 'Affiliate Program UAE | Daily UAE Jobs',
    description:
      'Discover the Daily UAE Jobs affiliate program, learn how referrals work and explore opportunities to promote career support services.',
  },
  '/referral-partner': {
    title: 'Affiliate Program UAE | Daily UAE Jobs',
    description:
      'Discover the Daily UAE Jobs affiliate program, learn how referrals work and explore opportunities to promote career support services.',
  },
  '/partners': {
    title: 'Daily UAE Jobs Partner Program | Refer and Earn',
    description:
      'Partner with Daily UAE Jobs. Refer candidates and businesses to our UAE career tools and earn commission on every deal.',
  },
};

// Private / utility routes that must not be indexed
const NOINDEX_PREFIXES = [
  '/login', '/register', '/forgot-password', '/reset-password',
  '/dashboard', '/admin', '/admin-access', '/account',
  '/cv', '/cv-builder', '/interview-test', '/interview-pack',
  '/job-apply-kit', '/linkedin-tips', '/checkout-success',
];

export default function RouteSeo() {
  const { pathname } = useLocation();
  const meta = ROUTE_SEO[pathname];
  const noindex = NOINDEX_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
  const canonical = `${SITE}${pathname === '/' ? '/' : pathname}`;

  const title = meta?.title || 'Daily UAE Jobs | Your Trusted Career Partner in Dubai, UAE';
  const description = meta?.description || DEFAULT_DESC;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <link rel="canonical" href={canonical} />
      )}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
    </Helmet>
  );
}
